import { useTranslate } from '@tolgee/react';
import { Tooltip } from 'antd';
import { useSession } from 'next-auth/react';
import { Dispatch, SetStateAction, useContext, useMemo, useState } from 'react';
import Hotkeys from 'react-hot-keys';
import styled from 'styled-components';

import { ArrowLeftSvg } from '../../../assets/icons';
import ArrowRightSvg from '../../../assets/icons/ArrowRightSvg';
import DragDropSvg from '../../../assets/icons/DragDropSvg';
import FlashSvg from '../../../assets/icons/FlashSvg';
import ShareSvg from '../../../assets/icons/ShareSvg';
import theme from '../../../assets/theme';
import useAutoFade from '../../../hooks/useAutoFade';
import { useBreakPoint } from '../../../hooks/useBreakPoint';
import { useMetrics } from '../../../hooks/useMetrics';
import usePreviewGame from '../../../hooks/usePreviewGame';
import useRouterMatch from '../../../hooks/useRouterMatch';
import { LiveActionsDisabledContext } from '../../../providers/LiveActionsDisabledProvider/LiveActionsDisabledProvider';
import { Game } from '../../../services/backendService/types';
import { toIcon } from '../../../utils/roundUtils';
import { zIndexes } from '../../../utils/zIndexes';
import { IconWrapper } from '../../_atoms/IconWrapper';
import ShareResultsModal from '../../_molecules/ShareResultsModal';
import { Slide, SlideType } from '../../SlideComponents/slide.types';
import {
  findRelatedSlidesWithoutIndex,
  getAggregatedSlidesAndActivities,
} from '../../SlideComponents/slideUtil';
import { toActivity } from '../LiveGame';

const LiveGameActionViewerButtons = ({
  slides,
  showActivityMedia,
  localGame,
  currentSlide,
  setShowMedia,
  setLocalGame,
}: {
  slides: Slide[];
  currentSlide: string;
  localGame: Game;
  setLocalGame: Dispatch<SetStateAction<Game | undefined>>;
  setShowMedia: Dispatch<SetStateAction<boolean>>;
  showActivityMedia: boolean;
}) => {
  const metrics = useMetrics();
  const { router, tenantId } = useRouterMatch();
  const numberOfSlides = useMemo(() => {
    return getAggregatedSlidesAndActivities(slides || []).length;
  }, [slides]);

  const session = useSession();

  const currentSlideRound = useMemo(() => {
    return currentSlide
      ? findRelatedSlidesWithoutIndex(currentSlide, slides)[0]
      : undefined;
  }, [currentSlide, slides]);
  const currentSlideRoundIndex = useMemo(() => {
    const slideRounds = getAggregatedSlidesAndActivities(slides || []);
    return currentSlideRound ? slideRounds.indexOf(currentSlideRound) : 0;
  }, [currentSlideRound, slides]);
  const canShowMedia = useMemo(() => {
    if (currentSlideRound?.type === 'slide') return false;
    return true;
  }, [currentSlideRound]);

  const { isInvisible } = useAutoFade({
    delay: 1000 * 2,
    eventListenerDependency: router.pathname,
  });

  const handleSkipBack = () => {
    if (currentSlideRoundIndex > 0) {
      metrics.logEvent('LiveGameViewer.GoBack.Clicked');
      const currentRelatedRounds = findRelatedSlidesWithoutIndex(currentSlide, slides);
      const firstRelatedRound = currentRelatedRounds[0];
      const firstRelatedRoundIndex = slides.findIndex(
        slide => slide.id === firstRelatedRound.id,
      );
      const previousRound = slides[firstRelatedRoundIndex - 1];
      const related = findRelatedSlidesWithoutIndex(previousRound.id, slides);
      const wasPlayed = related.some(r => r.wasPlayed);
      const newRound = related[wasPlayed ? related.length - 1 : 0];
      const updatedLocalGame = {
        ...localGame,
        currentRound: newRound.id,
      };
      setLocalGame(updatedLocalGame);
      setShowMedia(false);
    }
  };
  const handleSkipForward = () => {
    if (currentSlideRoundIndex + 1 < numberOfSlides) {
      metrics.logEvent('LiveGameViewer.GoForward.Clicked');
      const currentRelatedRounds = findRelatedSlidesWithoutIndex(currentSlide, slides);
      const lastRelatedRound = currentRelatedRounds[currentRelatedRounds.length - 1];
      const lastRelatedRoundIndex = slides.findIndex(
        round => round.id === lastRelatedRound.id,
      );
      const nextRound = slides[lastRelatedRoundIndex + 1];
      const related = findRelatedSlidesWithoutIndex(nextRound.id, slides);
      const wasPlayed = related.some(r => r.wasPlayed);

      setLocalGame({
        ...localGame,
        currentRound: related[wasPlayed ? related.length - 1 : 0].id,
      });
      setShowMedia(false);
    }
  };
  const { isPreview } = usePreviewGame();
  return (
    <LiveGameActionButtonContainer $isInvisible={isInvisible}>
      {currentSlide && (
        <ShareAction
          currentRound={currentSlide}
          gameId={localGame.id}
          tenantId={tenantId}
        />
      )}
      {canShowMedia && !isPreview && <LiveGameWidgetDivider />}
      {canShowMedia && !isPreview && (
        <ShowMediaAction
          setShowMedia={setShowMedia}
          showActivityMedia={showActivityMedia}
        />
      )}
      <LiveGameWidgetDivider />
      <LiveGameNavigationActions
        isNavigationDisabled={false}
        currentSlideType={currentSlideRound?.type}
        numberOfSlides={numberOfSlides}
        handleSkipBack={handleSkipBack}
        handleSkipForward={handleSkipForward}
        currentSlideRoundIndex={currentSlideRoundIndex}
      />
    </LiveGameActionButtonContainer>
  );
};

const ShowMediaAction = ({
  setShowMedia,
  showActivityMedia,
}: {
  setShowMedia: Dispatch<SetStateAction<boolean>>;
  showActivityMedia: boolean;
}) => {
  const { t } = useTranslate();
  const isLargerThan900 = useBreakPoint(900);
  const metrics = useMetrics();
  const session = useSession();
  return (
    <ShowMediaContainer>
      <Tooltip
        title={
          showActivityMedia
            ? t('Click to show the activity to start')
            : t('Click to show image or video related to activity')
        }
        mouseEnterDelay={2}
      >
        <LiveGameActionButton
          style={{ display: 'flex' }}
          onClick={() => {
            metrics.logEvent('LiveGameViewer.ShowMedia.Clicked');
            setShowMedia(e => !e);
          }}
        >
          <IconWrapper height={'25px'} width={'25px'}>
            {showActivityMedia ? <FlashSvg /> : <DragDropSvg />}
          </IconWrapper>
          {isLargerThan900 && (
            <span style={{ marginLeft: '5px' }}>
              {showActivityMedia ? t('Show results') : t('Show slide')}
            </span>
          )}
        </LiveGameActionButton>
      </Tooltip>
    </ShowMediaContainer>
  );
};

const ShareAction = ({
  gameId,
  tenantId,
  currentRound,
}: {
  gameId: string;
  tenantId: string;
  currentRound: string;
}) => {
  const { t } = useTranslate();
  const session = useSession();
  const metrics = useMetrics();
  const [showShareModal, setShowShareModal] = useState(false);
  return (
    <ShareActionsContainer>
      {showShareModal && (
        <ShareResultsModal
          gameId={gameId}
          tenantId={tenantId}
          setShowShareResults={setShowShareModal}
          showShareResults={showShareModal}
          gameTitle={t('Share results')}
          currentRoundId={currentRound}
        />
      )}
      <Tooltip title={t('Open link to this lesson to share')} mouseEnterDelay={2}>
        <LiveGameActionButton
          onClick={() => {
            metrics.logEvent('LiveGameViewer.Share.Open');
            setShowShareModal(true);
          }}
        >
          <IconWrapper height={'25px'} width={'25px'} style={{ marginRight: '10px' }}>
            <ShareSvg />
          </IconWrapper>

          {t('Share')}
        </LiveGameActionButton>
      </Tooltip>
    </ShareActionsContainer>
  );
};

const ShowMediaContainer = styled.div`
  display: grid;
  grid-auto-flow: column;
  gap: 5px;
  height: 100%;
`;
const ShareActionsContainer = styled.div`
  display: grid;
  grid-auto-flow: column;
  gap: 5px;
  height: 100%;
`;
const LiveGameWidgetDivider = styled.div`
  margin: 0;
  border-right: solid 1px ${theme.colors.grey};
  height: 100%;
`;

export default LiveGameActionViewerButtons;

export const LiveGameNavigationActions = ({
  currentSlideType,
  numberOfSlides,
  currentSlideRoundIndex,
  isNavigationDisabled,
  handleSkipBack,
  handleSkipForward,
  handleEscPressed,
}: {
  currentSlideType?: SlideType;
  numberOfSlides: number;
  currentSlideRoundIndex: number;
  isNavigationDisabled: boolean;
  handleSkipBack?: () => void;
  handleSkipForward?: () => void;
  handleEscPressed?: () => void;
}) => {
  const isLargerThan900 = useBreakPoint(900);

  const { arrowKeyIsDisabled } = useContext(LiveActionsDisabledContext);
  return (
    <NavigationButtons>
      {arrowKeyIsDisabled !== true && (
        <>
          <Hotkeys keyName="left" onKeyUp={handleSkipBack} />
          <Hotkeys keyName="up" onKeyUp={handleSkipBack} />
          <Hotkeys keyName="right" onKeyUp={handleSkipForward} />
          <Hotkeys keyName="down" onKeyUp={handleSkipForward} />
        </>
      )}
      <Hotkeys keyName="Escape" onKeyUp={() => handleEscPressed && handleEscPressed()} />
      <LiveGameActionButton
        $disabled={currentSlideRoundIndex < 1 || isNavigationDisabled}
        style={{ marginRight: '2px' }}
        onClick={handleSkipBack}
      >
        <IconWrapper
          color="white"
          width="40px"
          height="40px"
          style={{
            cursor: currentSlideRoundIndex < 1 ? 'not-allowed' : 'pointer',
          }}
        >
          <ArrowLeftSvg />
        </IconWrapper>
      </LiveGameActionButton>

      <NavigateSlideTitle className={'slide-title'}>
        {(currentSlideType !== 'slide' || !isLargerThan900) && (
          <IconWrapper
            color="white"
            style={{ marginRight: '2px', marginBottom: '-5px' }}
            width={'20px'}
            height={'20px'}
          >
            {toIcon(currentSlideType || '').icon}
          </IconWrapper>
        )}

        <span style={{ marginBottom: '-5px' }}>
          {isLargerThan900 && toActivity(currentSlideType || '')}
        </span>

        <span
          style={{
            whiteSpace: 'nowrap',
            width: '100%',
            fontSize: '16px',
            marginTop: '-5px',
          }}
        >
          {currentSlideRoundIndex + 1}/{numberOfSlides}
        </span>
      </NavigateSlideTitle>
      <LiveGameActionButton
        style={{ marginRight: '12px' }}
        $disabled={currentSlideRoundIndex >= numberOfSlides - 1 || isNavigationDisabled}
        onClick={() => {
          if (handleSkipForward) handleSkipForward();
        }}
      >
        <IconWrapper
          color="white"
          width="40px"
          height="40px"
          style={{
            cursor:
              currentSlideRoundIndex >= numberOfSlides - 1 ? 'not-allowed' : 'pointer',
          }}
        >
          <ArrowRightSvg />
        </IconWrapper>
      </LiveGameActionButton>
    </NavigationButtons>
  );
};

export const LiveGameActionButton = styled.button<{
  $isActive?: boolean;
  $disabled?: boolean;
}>`
  border: none;
  transition-duration: 0.5s;
  min-height: 30px;
  height: 100%;
  padding: 5px;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  min-width: 60px;
  border-radius: 10px;
  font-size: 1.2em;
  color: white;
  background-color: ${({ $isActive }) => ($isActive ? '#656570;' : 'transparent')};
  filter: ${({ $disabled }) => ($disabled ? 'brightness(50%)' : 'brightness(100%)')};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  svg {
    cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  }
  outline: none;
  &:hover {
    background-color: #656570;
  }
`;
export const LiveGameActionButtonContainer = styled.div<{ $isInvisible: boolean }>`
  opacity: ${({ $isInvisible }) => ($isInvisible ? '0' : '1')};
  transition: 0.3s;
  position: fixed;
  bottom: 10px;
  transform: translateX(0%);
  right: 10px;
  justify-content: space-evenly;
  font-size: 12px;
  display: grid;
  align-items: center;
  gap: 12px;
  grid-auto-flow: column;
  justify-content: space-evenly;
  background-color: ${theme.colors.translucentLightBlack};
  padding: 5px;
  padding-left: 5px;
  padding-right: 5px;
  border-radius: 10px;
  box-shadow: 0px 2px 10px 0px rgba(0, 0, 0, 0.25);
  z-index: ${zIndexes('PRESENTER_ACTION_BUTTONS')};
  @media (max-width: 900px) {
    gap: 2px;
  }
`;

const NavigationButtons = styled.div`
  display: flex;
  align-items: center;
  height: 50px;
  color: white;
`;

const NavigateSlideTitle = styled.div`
  width: 170px;
  display: flex;
  text-align: center;
  height: 100%;
  flex-wrap: wrap;
  justify-content: center;
  font-size: 20px;
  align-items: center;
  cursor: default;
  svg {
    cursor: default;
  }
  font-weight: bold;
  @media (max-width: 900px) {
    width: 40px;
  }
`;
